import { createServerFn } from "@tanstack/react-start";
import { getRequest, setResponseHeader } from "@tanstack/react-start/server";
import { getSessionFromRequest, commitSession } from "@/lib/session";
import {
  generateCodeVerifier,
  generateCodeChallenge,
  buildAuthorizationUrl,
  exchangeCodeForTokens,
  decodeJwtPayload,
} from "@/lib/auth";
import { getEnv, isDevAuth } from "@/lib/env";
import { getDb } from "@/lib/services";
import { upsertUser } from "@/db/user";
import { redirect } from "@tanstack/react-router";

export const loginLoader = createServerFn({ method: "GET" }).handler(async () => {
  const request = getRequest();
  const session = await getSessionFromRequest(request);

  if (session.userId) {
    throw redirect({ to: "/" });
  }

  if (await isDevAuth()) {
    throw redirect({ to: "/dev/login" });
  }

  const env = await getEnv();
  const clientId = env.GOOGLE_CLIENT_ID;

  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  const redirectUri = new URL("/auth/callback", request.url).toString();
  const authUrl = buildAuthorizationUrl(clientId, redirectUri, codeChallenge);

  const cookie = await commitSession({ ...session, codeVerifier });
  setResponseHeader("Set-Cookie", cookie);

  throw redirect({ href: authUrl });
});

export const authCallbackLoader = createServerFn({ method: "GET" }).handler(async () => {
  const request = getRequest();
  const url = new URL(request.url);
  const code = url.searchParams.get("code");
  const error = url.searchParams.get("error");

  const session = await getSessionFromRequest(request);

  if (error || !code) {
    const { codeVerifier: _, ...rest } = session;
    const cookie = await commitSession(rest);
    setResponseHeader("Set-Cookie", cookie);
    throw redirect({ to: "/" });
  }

  const codeVerifier = session.codeVerifier;
  if (!codeVerifier) {
    const cookie = await commitSession({});
    setResponseHeader("Set-Cookie", cookie);
    throw redirect({ to: "/" });
  }

  const env = await getEnv();
  const clientId = env.GOOGLE_CLIENT_ID;
  const clientSecret = Object.hasOwn(env.GOOGLE_CLIENT_SECRET, "get")
    ? await env.GOOGLE_CLIENT_SECRET.get()
    : env.GOOGLE_CLIENT_SECRET;
  const redirectUri = new URL("/auth/callback", request.url).toString();

  try {
    const tokens = await exchangeCodeForTokens(
      clientId,
      clientSecret,
      code,
      redirectUri,
      codeVerifier,
    );

    const claims = decodeJwtPayload(tokens.id_token);

    const db = await getDb();
    await upsertUser(db, claims.sub, claims.email ?? "", claims.name ?? "");

    const cookie = await commitSession({
      userId: claims.sub,
      email: claims.email ?? "",
      name: claims.name ?? "",
      token: tokens.id_token,
    });
    setResponseHeader("Set-Cookie", cookie);
    throw redirect({ to: "/" });
  } catch (err) {
    if (err instanceof Response || (err && typeof err === "object" && "to" in err)) {
      throw err;
    }
    console.error("Token exchange failed:", err);
    const cookie = await commitSession({});
    setResponseHeader("Set-Cookie", cookie);
    throw redirect({ to: "/" });
  }
});

export const devLoginGuard = createServerFn({ method: "GET" }).handler(async () => {
  if (!(await isDevAuth())) {
    throw redirect({ to: "/login" });
  }

  const request = getRequest();
  const session = await getSessionFromRequest(request);
  if (session.userId) {
    throw redirect({ to: "/" });
  }
});

export const devLoginAs = createServerFn({ method: "POST" })
  .inputValidator((data: { userId: string; email: string; name: string }) => data)
  .handler(async ({ data }) => {
    if (!(await isDevAuth())) {
      throw new Error("Dev auth is not enabled");
    }

    const db = await getDb();
    await upsertUser(db, data.userId, data.email, data.name);

    const cookie = await commitSession({
      userId: data.userId,
      email: data.email,
      name: data.name,
      token: "dev-token-not-valid",
    });
    setResponseHeader("Set-Cookie", cookie);
  });
